/**
 * @file Builds the upload handler that parses, validates and builds rows from a CSV import file.
 */

import { readString } from "react-papaparse";

import parseCsv from "./parseCsv";
import type { ParseRow, BuildRow, ValidateRow, UploadRequest, UploadRequestOptions } from "./types";

const UPLOAD_SUCCESS_BODY = "ok";

type ParseOutput = {
  data: string[][];
};

// The react-papaparse types declare only the callback form, but the
// parser returns the result of a string input at once.
const readCsvString = readString as unknown as (csvString: string) => ParseOutput;

type CallbackUpload = (file: UploadRequestOptions["file"]) => void;

/**
 * Builds an upload request handler that reads a CSV file, parses its rows and reports the built items.
 */
const getCustomRequest = <P, T>(
  parse: ParseRow<P>,
  validate: ValidateRow<P>,
  build: BuildRow<P, T>,
  callbackSuccess: (items: T[]) => void,
  callbackUpload?: CallbackUpload,
): UploadRequest => {
  /**
   * Reads the uploaded file, parses its rows into items, and reports success to the upload input.
   */
  const uploadRequest: UploadRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      const hasCallbackUpload = !!callbackUpload;

      if (hasCallbackUpload) {
        callbackUpload(file);
      }

      const reader = new FileReader();

      /**
       * Parses the read CSV text and reports the built items to the caller.
       */
      reader.onload = () => {
        const text = reader.result as string;

        const { data } = readCsvString(text.trim());
        const items = parseCsv(data, parse, validate, build);

        callbackSuccess(items);
      };

      // The upload input hands a file object to the request; the string
      // form of the option is for the remote mode of the input.
      reader.readAsText(file as Blob);
      onSuccess(UPLOAD_SUCCESS_BODY);
    }, 0);
  };

  return uploadRequest;
};

export default getCustomRequest;
